import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Single-select dropdown (matches mobile FormSelect).
 */
export default function FormSelect({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  searchable = false,
  error = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const mergedOptions = useMemo(() => {
    if (!value || options.some((option) => option.value === value)) return options;
    return [{ value, label: value }, ...options];
  }, [options, value]);

  const selected = mergedOptions.find((option) => option.value === value);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return mergedOptions;
    return mergedOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(term) ||
        option.value.toLowerCase().includes(term),
    );
  }, [mergedOptions, query]);

  const triggerClass = `themed-input w-full px-4 py-3 rounded-lg text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] ${
    error ? 'border-red-500 focus:ring-red-500' : ''
  }`;

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setQuery('');
          setOpen(true);
        }}
        className={triggerClass}
      >
        <span className={`truncate ${selected ? 'text-[var(--color-text)]' : 'text-[var(--color-text-muted)]'}`}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown className="w-4 h-4 text-[var(--color-text-muted)] shrink-0 ml-2" />
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="elevated-card w-full max-w-md rounded-xl max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {searchable ? (
              <div className="p-3 border-b border-[var(--color-border)]">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search..."
                  autoFocus
                  className="themed-input w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                />
              </div>
            ) : null}
            <div className="overflow-y-auto flex-1 p-1">
              {filtered.length === 0 ? (
                <p className="px-4 py-3 text-sm text-[var(--color-text-muted)]">No matches found</p>
              ) : (
                filtered.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`w-full text-left px-4 py-2.5 text-sm rounded-lg hover:bg-[var(--color-primary-soft)] text-[var(--color-text)] ${
                      value === option.value ? 'bg-[var(--color-primary)]/40' : ''
                    }`}
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                      setQuery('');
                    }}
                  >
                    {option.label}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
